import type { ChangeEvent } from "react";
import { useMemo } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { getNormalizedBirthDateChange } from "@utils/dateFormat";
import { dateFormatRegex, maxYearValid, minYearValid, over18Valid } from "@utils/formControls";

type TControllerOnChange = (payload: unknown) => void;

export const useLabeledDateTextField = (isRequired?: boolean) => {
    const { t } = useTranslation(["validation", "control"]);

    const onLabeledDateTextFieldChange = (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        isReadOnly: boolean,
        previousValue: string,
        controllerOnChange: TControllerOnChange
    ): void => {
        if (isReadOnly) {
            controllerOnChange(event);

            return;
        }
        const input = event.currentTarget;
        const { value, caret } = getNormalizedBirthDateChange(previousValue, input.value, input.selectionStart);
        flushSync(() => {
            controllerOnChange(value);
        });
        const clamped = Math.min(Math.max(caret, 0), input.value.length);
        input.setSelectionRange(clamped, clamped);
    };

    const rules = useMemo(
        () => ({
            required: isRequired ? t("validation:required") : false,
            validate: (value: string | undefined) => {
                const digits = (value ?? "").replace(/\D/g, "");
                if (digits.length === 0) {
                    return undefined;
                }
                if (digits.length < 8) {
                    return t("control:incorrectDateFormat");
                }
                const valueToFormat = value ?? "";

                return (
                    dateFormatRegex(valueToFormat) ??
                    over18Valid(valueToFormat) ??
                    minYearValid(valueToFormat) ??
                    maxYearValid(valueToFormat)
                );
            },
        }),
        [isRequired, t]
    );

    return {
        onLabeledDateTextFieldChange,
        rules,
    };
};
